import { useQuery } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import {
  HomeBannerConnectCRM,
  HomeBannerUpgradePlan,
} from '~/components/home/Banners';
import { TimePeriodContext } from '~/components/home/context/TimePeriodContext';
import { HomeFooter } from '~/components/home/HomeFooter';
import { HomeHeader } from '~/components/home/HomeHeader';
import {
  HomeSectionDataOverview,
  HomeSectionImplementation,
  HomeSectionNotifications,
} from '~/components/home/HomeSection';
import {
  HomeSidebar,
  HomeSidebarNews,
  HomeSidebarNextSteps,
  HomeSidebarUsage,
} from '~/components/home/HomeSidebar';
import DataOverviewQuery from '~/components/home/queries/DataOverview.graphql';
import useOnboardingStatus from '~/hooks/account/useOnboardingStatus';

import homeQuery from './homeQuery.graphql';
import { DashboardProps } from './types';

export const HomePageContent = ({ data }: DashboardProps) => {
  const { hasConnectors, shouldOnboard } = useOnboardingStatus();
  const isOnboarding = shouldOnboard;

  const [timePeriod, setTimePeriod] = useState('60');
  const start = moment()
    .subtract(Number(timePeriod), 'days')
    .format('YYYY-MM-DD');
  const end = moment().format('YYYY-MM-DD');

  const {
    data: dataOverview,
    loading: dataOverviewLoading,
    refetch: refetchDataOverview,
  } = useQuery(DataOverviewQuery, { variables: { start, end } });

  useEffect(() => {
    const start = moment()
      .subtract(Number(timePeriod), 'days')
      .format('YYYY-MM-DD');
    const end = moment().format('YYYY-MM-DD');

    refetchDataOverview({ start, end });
  }, [timePeriod]);

  return (
    <>
      <Helmet>
        <title>Home</title>
      </Helmet>
      <div className="w-full h-full @container/page ck-scrollbar ck-scrollbar--vertical">
        <div className="h-full min-w-0 min-h-0 bg-white @4xl/page:grid grid-cols-[1fr_25rem]">
          <div className="min-w-0 min-h-0 p-8 ck-scrollbar ck-scrollbar--vertical space-y-12">
            <HomeHeader
              account={data?.account}
              hasAccountIssue={!!data?.accountNotifications.length}
              isOnboarding={isOnboarding}
              user={data?.viewer.user}
            />
            <HomeSectionNotifications
              accountConnectors={data?.accountConnectors}
              notifications={data?.accountNotifications}
            />
            {isOnboarding ? <HomeSectionImplementation /> : null}
            <div className="relative space-y-12">
              {!hasConnectors ? <HomeBannerConnectCRM /> : null}
              {!isOnboarding ? <HomeBannerUpgradePlan /> : null}
              <TimePeriodContext.Provider value={{ timePeriod, setTimePeriod }}>
                <HomeSectionDataOverview
                  data={dataOverview?.rollupCounts}
                  isLoading={dataOverviewLoading}
                />
              </TimePeriodContext.Provider>
            </div>

            <HomeFooter />
          </div>
          <HomeSidebar>
            <HomeSidebarUsage />
            {!isOnboarding ? <HomeSidebarNextSteps /> : null}
            <HomeSidebarNews news={data?.news} />
          </HomeSidebar>
        </div>
      </div>
    </>
  );
};

export const HomePage = () => {
  return (
    <Query fetchPolicy="network-only" query={homeQuery}>
      {(props: any) => <HomePageContent {...props} />}
    </Query>
  );
};
